import React, { useEffect, useMemo } from 'react'
import { v4 as uniqueId } from 'uuid'
import { DNABox, DNAButton, DNAButtonProps, DNAText } from '@alucio/lux-ui'
import DNAPopover from 'src/components/DNA/Popover/DNAPopover'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'

import { usePresentationBuilderState } from '../state/PresentationBuilderStateProvider'
import Header from './Header'
import SlidesGroupTarget from 'src/components/DnD/Clone/PresentationBuilder/SlidesGroupTarget'
import FindReplacement from './FindReplacement/FindReplacement'
import ActiveUser from 'src/state/global/ActiveUser'
import { useTenant } from 'src/state/redux/selector/tenant'
import { useDispatch } from 'src/state/redux'
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal'
import CustomDeckAcknowledgementModal from 'src/components/DNA/Modal/CustomDeckAcknowledgementModal'
import ComplianceStateProvider from 'src/state/context/Compliance/ComplianceStateProvider'

export interface Action extends DNAButtonProps {
  label?: string
  fill?: boolean
  hidden?: boolean
  tooltip?: string
}

export const usePresentationBuilderSharedResources = () => {
  const containerMargin = 15
  const getActionEls: (actions: Action[]) => JSX.Element[] = (actions) => {
    return actions.filter(action => !action.hidden).map(action => {
      const {
        disabled = false,
        iconLeft,
        onPress,
        label,
        appearance = 'ghost',
        status = 'tertiary',
        fill,
        style,
        size = 'md',
        tooltip,
        testID,
      } = action

      const actionButton = (
        <DNAButton
          testID={testID}
          appearance={appearance}
          status={status}
          size={size}
          iconLeft={iconLeft}
          onPress={onPress}
          disabled={disabled}
          style={style}
          children={label}
        />
      )

      return (
        <DNABox
          key={uniqueId()}
          fill={fill}
        >
          {tooltip
            ? <DNAPopover placement="top" disablePopover={['tabletPWA']}>
              <DNAPopover.Anchor>
                {actionButton}
              </DNAPopover.Anchor>
              <DNAPopover.Content>
                <DNAText
                  style={{ color: colors['color-text-white'] }}
                >{tooltip}</DNAText>
              </DNAPopover.Content>

            </DNAPopover>
            : actionButton}
        </DNABox>
      )
    })
  }

  return {
    containerMargin,
    getActionEls,
  }
}

const PresentationEditor: React.FC = () => {
  const { builderMode, customDeck, compliancePanelVisible } = usePresentationBuilderState()
  const isSelectionMode = builderMode === 'selection'
  const tenant = useTenant(ActiveUser.user?.tenantId || '');
  const dispatch = useDispatch()

  const maxWidth = useMemo(() => {
    if (!isSelectionMode) return undefined
    // When compliance panel is open: ~200px (sm thumbnail + padding) + 420px (panel)
    if (compliancePanelVisible) return 650
    return 600
  }, [isSelectionMode, compliancePanelVisible])

  useEffect(() => {
    if (!tenant) return
    if (customDeck?.meta.version.requiresReview) return // if review required we do not show modal
    if (
      tenant.tenant.config.customDeckAcknowledgement?.shouldShow &&
      tenant.tenant.config.customDeckAcknowledgement?.content?.length
    ) {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: false,
        component: (props) => (
          < CustomDeckAcknowledgementModal
            {...props}
            value={tenant.tenant.config.customDeckAcknowledgement?.content!}
          />
        ),
      }))
    }
  }, [])

  return (
    <ComplianceStateProvider>
      <FindReplacement />
      <DNABox
        fill
        appearance="col"
        style={{
          maxWidth,
          borderLeftColor: colors['color-gray-80'],
          borderLeftWidth: isSelectionMode ? 2 : undefined,
        }}
      >
        <Header />
        <SlidesGroupTarget />
      </DNABox>
    </ComplianceStateProvider>
  )
}
export default PresentationEditor
